import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Chip,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  listAuditLogs,
  listTenantOrganizations,
} from "../../services/organizationService";
import { useSelector } from "react-redux";
import {
  selectActiveOrganizationId,
  selectIsSuperAdmin,
  selectMemberships,
  selectRole1,
} from "../../redux/authSlice";

const PAGE_SIZE = 50;

const AuditLogsPage: React.FC = () => {
  const activeOrganizationId = useSelector(selectActiveOrganizationId);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const memberships = useSelector(selectMemberships);
  const hierarchyLevel = useSelector(selectRole1);
  const canView =
    isSuperAdmin ||
    String(hierarchyLevel) === "1" ||
    memberships.some(
      (m) =>
        m.canViewAuditLogs ||
        m.roleSlug === "org_admin" ||
        m.roleSlug === "sub_admin" ||
        m.roleSlug === "super_admin"
    );

  const [orgs, setOrgs] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [action, setAction] = useState("");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [flowOnly, setFlowOnly] = useState(false);
  const [showNoise, setShowNoise] = useState(false);
  const [orgFilter, setOrgFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState<string>("");


  useEffect(() => {
    if (
      activeOrganizationId &&
      activeOrganizationId !== "all" &&
      activeOrganizationId !== "*"
    ) {
      setOrgFilter(String(activeOrganizationId));
    } else if (isSuperAdmin) {
      setOrgFilter("all");
    }
  }, [activeOrganizationId, isSuperAdmin]);

  useEffect(() => {
    (async () => {
      const res = await listTenantOrganizations();
      if (res?.success === 1) {
        let list = res.data || [];
        if (!isSuperAdmin) {
          const allowed = new Set(
            (memberships || []).map((m) => String(m.organizationId))
          );
          list = list.filter((o: any) => allowed.has(String(o._id)));
        }
        setOrgs(list);
      }
    })();
  }, [isSuperAdmin, memberships]);

  const orgNameById = useMemo(() => {
    const map: Record<string, string> = {};
    orgs.forEach((o) => {
      map[String(o._id)] = o.name || o.brandName || o.slug || String(o._id);
    });
    return map;
  }, [orgs]);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    setError("");
    try {
      const query: Record<string, any> = {
        limit: PAGE_SIZE,
        page,
        meaningful: showNoise ? "0" : "1",
      };
      if (showNoise) query.showNoise = "1";
      if (action.trim()) query.action = action.trim();
      if (search.trim()) query.q = search.trim();
      if (from) query.from = from;
      if (to) query.to = to;
      if (flowOnly) query.flow = "1";
      if (orgFilter && orgFilter !== "all") {
        query.organizationId = orgFilter;
      } else if (isSuperAdmin) {
        query.organizationId = "all";
      }

      const res = await listAuditLogs(query);
      if (res?.success === 1) {
        setItems(res.data?.items || []);
        setTotal(Number(res.data?.total || 0));
      } else {
        setItems([]);
        setTotal(0);
        setError(res?.message || "Failed to load audit logs");
      }
    } catch (e: any) {
      setItems([]);
      setTotal(0);
      setError(e?.message || "Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, [
    canView,
    page,
    action,
    search,
    from,
    to,
    flowOnly,
    showNoise,
    orgFilter,
    isSuperAdmin,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>
          You do not have permission to view audit logs. Ask an admin to enable
          audit access on your organization membership.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "calc(100vh - 68px)",
        minHeight: 0,
        minWidth: 0,
        overflow: "auto",
        p: 3,
        boxSizing: "border-box",
      }}
    >
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
        Audit logs
      </Typography>
      <Typography sx={{ mb: 2, color: "#6B7280", fontSize: 13 }}>
        Tracks who did what in the panel: calls, lead stage/assign, remarks,
        team edits, org changes, login, and more. Only push-notification token
        noise is hidden by default.
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Organization</InputLabel>
            <Select
              label="Organization"
              value={
                orgFilter === "all" ||
                orgs.some((o) => String(o._id) === String(orgFilter))
                  ? orgFilter
                  : ""
              }
              onChange={(e) => {
                setPage(1);
                setOrgFilter(String(e.target.value));
              }}
            >
              {isSuperAdmin && (
                <MenuItem value="all">All organizations</MenuItem>
              )}
              {orgs.map((o) => (
                <MenuItem key={String(o._id)} value={String(o._id)}>
                  {o.name || o.brandName || o.slug}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={(e) => {
              setPage(1);
              setFrom(e.target.value);
            }}
            sx={{ width: 160 }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={(e) => {
              setPage(1);
              setTo(e.target.value);
            }}
            sx={{ width: 160 }}
          />

          <TextField
            size="small"
            label="Action contains"
            value={action}
            onChange={(e) => {
              setPage(1);
              setAction(e.target.value);
            }}
            placeholder="e.g. Lead, Role, organization"
            sx={{ minWidth: 220 }}
          />
          <TextField
            size="small"
            label="Search actor / entity"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            placeholder="Name, email, action…"
            sx={{ minWidth: 180 }}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={flowOnly}
                onChange={(e) => {
                  setPage(1);
                  setFlowOnly(e.target.checked);
                }}
              />
            }
            label="Org / automation only"
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={showNoise}
                onChange={(e) => {
                  setPage(1);
                  setShowNoise(e.target.checked);
                }}
              />
            }
            label="Show system noise"
          />

          <Button variant="outlined" onClick={load} disabled={loading}>
            {loading ? "Loading…" : "Refresh"}
          </Button>
        </Box>
      </Paper>

      <Box
        sx={{
          mb: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography sx={{ color: "#6B7280", fontSize: 13 }}>
          {total} event{total === 1 ? "" : "s"}
          {orgFilter && orgFilter !== "all"
            ? ` · ${orgNameById[orgFilter] || orgFilter}`
            : isSuperAdmin
              ? " · all organizations"
              : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            size="small"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Prev
          </Button>
          <Typography sx={{ fontSize: 13 }}>
            Page {page} / {totalPages}
          </Typography>
          <Button
            size="small"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </Box>
      </Box>

      {error ? (
        <Typography sx={{ mb: 2, color: "#B91C1C", fontSize: 13 }}>
          {error}
        </Typography>
      ) : null}

      <Paper
        sx={{
          width: "100%",
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: "visible",
        }}
      >
        <Table size="small" sx={{ minWidth: 960, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 140 }}>When</TableCell>
              <TableCell sx={{ width: 120 }}>Organization</TableCell>
              <TableCell sx={{ width: 180 }}>Who</TableCell>
              <TableCell sx={{ width: 180 }}>What they did</TableCell>
              <TableCell sx={{ width: 240 }}>Data changed</TableCell>
              <TableCell sx={{ width: 90 }}>IP</TableCell>
              <TableCell sx={{ width: 80 }}>Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row) => {
              const orgLabel =
                row.organizationName ||
                orgNameById[String(row.organizationId || "")] ||
                (row.organizationId ? String(row.organizationId) : "—");
              const ok =
                row.after?.success === true ||
                row.after?.success === 1 ||
                (row.after?.statusCode >= 200 && row.after?.statusCode < 400) ||
                (!row.after?.statusCode &&
                  (row.changeSummary || row.action?.includes("organization")));
              const who =
                row.actorName ||
                row.actorEmail ||
                (row.actorUserId
                  ? String(row.actorUserId).slice(0, 8)
                  : "System / unknown");
              const changes: {
                field: string;
                label?: string;
                from?: any;
                to?: any;
              }[] = Array.isArray(row.changes) ? row.changes : [];
              const summary =
                row.changeSummary ||
                (changes.length
                  ? changes
                      .slice(0, 4)
                      .map(
                        (c) =>
                          `${c.label || c.field}: ${c.from ?? "—"} → ${c.to ?? "—"}`
                      )
                      .join("; ")
                  : "");
              const expanded = expandedId === String(row._id);
              return (
                <React.Fragment key={row._id}>
                  <TableRow
                    hover
                    onClick={() =>
                      setExpandedId(expanded ? "" : String(row._id))
                    }
                    sx={{ cursor: changes.length || summary ? "pointer" : "default" }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap", fontSize: 12 }}>
                      {row.createdAt
                        ? new Date(row.createdAt).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 12,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={orgLabel}
                    >
                      {orgLabel}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, overflow: "hidden" }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {who}
                        </span>
                        {row.actorEmail && row.actorName ? (
                          <span
                            style={{
                              color: "#6B7280",
                              fontSize: 11,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={row.actorEmail}
                          >
                            {row.actorEmail}
                          </span>
                        ) : null}
                        {row.actorRole ? (
                          <Chip
                            size="small"
                            label={String(row.actorRole).replace(/_/g, " ")}
                            variant="outlined"
                            sx={{ width: "fit-content", height: 20, maxWidth: "100%" }}
                          />
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, overflow: "hidden" }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={row.action}
                        >
                          {row.action}
                        </span>
                        {row.httpAction && row.httpAction !== row.action ? (
                          <span
                            style={{
                              color: "#9CA3AF",
                              fontSize: 11,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={row.httpAction}
                          >
                            {row.httpAction}
                          </span>
                        ) : null}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 12,
                        overflow: "hidden",
                        wordBreak: "break-word",
                      }}
                      title={summary || undefined}
                    >
                      {summary ? (
                        <span
                          style={{
                            color: "#111827",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {summary}
                        </span>
                      ) : (
                        <span style={{ color: "#9CA3AF" }}>—</span>
                      )}
                      {changes.length > 4 ? (
                        <span style={{ color: "#6B7280", fontSize: 11 }}>
                          {" "}
                          (+{changes.length - 4} more — click row)
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                      {row.ip || "—"}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        size="small"
                        label={
                          row.after?.statusCode != null
                            ? String(row.after.statusCode)
                            : ok
                              ? "ok"
                              : "—"
                        }
                        color={ok ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                  {expanded && changes.length > 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ bgcolor: "#F9FAFB" }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 1 }}>
                          Field changes
                        </Typography>
                        <Box
                          component="ul"
                          sx={{
                            m: 0,
                            pl: 2,
                            fontSize: 12,
                            color: "#374151",
                            wordBreak: "break-word",
                          }}
                        >
                          {changes.map((c, idx) => (
                            <li key={`${c.field}-${idx}`}>
                              <strong>{c.label || c.field}</strong>:{" "}
                              <span style={{ color: "#9CA3AF" }}>
                                {c.from === undefined || c.from === null || c.from === ""
                                  ? "—"
                                  : String(c.from)}
                              </span>
                              {" → "}
                              <span>{String(c.to ?? "—")}</span>
                            </li>
                          ))}
                        </Box>
                        {row.entityType || row.entityId ? (
                          <Typography
                            sx={{
                              mt: 1,
                              fontSize: 11,
                              color: "#6B7280",
                              wordBreak: "break-all",
                            }}
                          >
                            Entity: {row.entityType || "—"}
                            {row.entityId ? ` / ${row.entityId}` : ""}
                          </Typography>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </React.Fragment>
              );
            })}
            {!items.length && !loading && (
              <TableRow>
                <TableCell colSpan={7}>
                  No panel activity for this filter. Try another organization,
                  clear dates, or enable “Show system noise”.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default AuditLogsPage;
